/**
 * JourneyPublicationPickerPage — PD-GT-02 (Wave 2).
 *
 * Picker for "發起揪團": lists the actor's own PUBLISHED journey publications.
 * Select a card, then confirm via footer CTA → /group-trips/new?publicationId=<id>.
 *
 * Contract rules:
 * - Only own + PUBLISHED publications qualify (GroupTripPublicationEligibilityPolicy).
 * - Bare /group-trips/new without publicationId must NOT be submitted.
 * - Implementation: journeyApi.listMine() + journeyPublicationApi.getByJourney() FE composition.
 * - No new backend API required (Wave 2 FE-only).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { journeyApi } from '../../api/journeyApi';
import { journeyPublicationApi } from '../../api/journeyPublicationApi';
import { normalizeMyJourneySummary } from '../my-journeys/myJourneysModel';
import './group-trip.css';

function resolvePublicationId(pub) {
  const status = pub?.status || '';
  const id = pub?.publicationId != null ? String(pub.publicationId) : null;
  // Must be numeric snowflake — CreateGroupTripPage rejects non-numeric IDs
  if (status !== 'PUBLISHED' || !id || !/^[1-9]\d*$/.test(id)) return null;
  return id;
}

export default function JourneyPublicationPickerPage() {
  const { t } = useTravelTranslation();
  const navigate = useNavigate();
  const [phase, setPhase] = useState('loading');
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');
  const [selectedPublicationId, setSelectedPublicationId] = useState(null);
  const generation = useRef(0);

  const load = useCallback(async () => {
    const token = ++generation.current;
    setPhase('loading');
    setError('');
    setSelectedPublicationId(null);
    try {
      const raw = await journeyApi.listMine();
      if (token !== generation.current) return;
      const journeys = Array.isArray(raw) ? raw.map(normalizeMyJourneySummary) : [];

      // FE composition: fetch publication status per journey in parallel
      const enriched = await Promise.all(
        journeys.map(async (journey) => {
          try {
            const pub = await journeyPublicationApi.getByJourney(journey.journeyId);
            const publicationId = resolvePublicationId(pub);
            return { ...journey, publicationId };
          } catch {
            return { ...journey, publicationId: null };
          }
        }),
      );
      if (token !== generation.current) return;
      // Only own PUBLISHED journeys with a valid publicationId
      setItems(enriched.filter((j) => Boolean(j.publicationId)));
      setPhase('ready');
    } catch {
      if (token !== generation.current) return;
      setError(t('travel.common.loadFailed'));
      setPhase('error');
    }
  }, [t]);

  useEffect(() => {
    load();
    return () => { generation.current += 1; };
  }, [load]);

  function continueWithSelection() {
    if (!selectedPublicationId) return;
    navigate(`/group-trips/new?publicationId=${encodeURIComponent(selectedPublicationId)}`);
  }

  return (
    <main className="group-trips group-trips--picker" aria-labelledby="picker-heading">
      <header className="group-trips__picker-topbar">
        <Link
          className="group-trips__picker-back"
          to="/group-trips"
          aria-label={t('travel.common.back')}
          data-testid="picker-back"
        >
          ‹
        </Link>
        <h1 id="picker-heading" className="group-trips__picker-title">選擇行程</h1>
        <span className="group-trips__picker-topbar-spacer" aria-hidden="true" />
      </header>

      <div className="group-trips__picker-body">
        <p className="group-trips__picker-lead">從已發布的行程發起揪團</p>
        <p className="group-trips__picker-sub">只顯示你擁有且已公開發布的行程。</p>

      {phase === 'loading' ? (
        <section className="group-trips__state" aria-live="polite" aria-busy="true">
          <p>{t('travel.common.loading')}</p>
        </section>
      ) : null}

      {phase === 'error' ? (
        <section className="group-trips__state group-trips__state--error" role="alert">
          <p>{error}</p>
          <button type="button" onClick={load}>{t('travel.common.retry')}</button>
        </section>
      ) : null}

      {phase === 'ready' && items.length === 0 ? (
        <section className="group-trips__state">
          <p>你目前沒有已發布的行程。</p>
          <div className="group-trips__picker-constraint" data-testid="picker-publish-hint">
            找不到行程？請先發布一個公開行程，再回來發起揪團。
          </div>
          <Link className="group-trips__link" to="/journeys">前往我的行程</Link>
        </section>
      ) : null}

      {phase === 'ready' && items.length > 0 ? (
        <>
          <ul className="group-trips__list group-trips__list--picker" aria-label="可用的已發布行程">
            {items.map((item) => {
              const selected = selectedPublicationId === item.publicationId;
              return (
                <li className="group-trips__card group-trips__card--picker" key={item.journeyId}>
                  <button
                    type="button"
                    className={
                      `group-trips__picker-card-btn${selected ? ' is-selected' : ''}`
                    }
                    aria-pressed={selected}
                    onClick={() => setSelectedPublicationId(item.publicationId)}
                    data-testid={`journey-pick-${item.journeyId}`}
                  >
                    <div className="group-trips__picker-card-head">
                      <h2>{item.title}</h2>
                      <span className="group-trips__picker-badge">已發布</span>
                    </div>
                    {item.destination ? <p className="group-trips__picker-meta">{item.destination}</p> : null}
                    {item.dateRange ? (
                      <p className="group-trips__picker-meta">{item.dateRange}</p>
                    ) : null}
                    <div className="group-trips__picker-card-foot">
                      <span className="group-trips__hint">公開行程 · 可建立揪團</span>
                      <span className="group-trips__picker-mark" aria-hidden="true">{selected ? '✓' : '○'}</span>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>

          <p className="group-trips__picker-constraint" data-testid="picker-publish-hint">
            找不到行程？請先發布一個公開行程，再回來發起揪團。
          </p>

          <div className="group-trips__picker-footer">
            <button
              type="button"
              className="group-trips__picker-next"
              disabled={!selectedPublicationId}
              onClick={continueWithSelection}
              data-testid="picker-next-cta"
            >
              下一步：選擇揪團類型
            </button>
          </div>
        </>
      ) : null}
      </div>
    </main>
  );
}
